export type ParsedAgentRef =
  | { kind: 'owned'; agentId: string }
  | { kind: 'shared'; ownerUsername: string; slug: string }

const OWNED_AGENT_REF_PREFIX = 'agent/'
const SHARED_AGENT_REF_PREFIX = 'shared/'

export function formatOwnedAgentRef(agentId: string): string {
  return `${OWNED_AGENT_REF_PREFIX}${agentId}`
}

export function formatSharedAgentRef(ownerUsername: string, slug: string): string {
  return `${SHARED_AGENT_REF_PREFIX}${ownerUsername}/${slug}`
}

export function parseAgentRef(value: string): ParsedAgentRef | null {
  const trimmed = value.trim()
  if (trimmed.startsWith(OWNED_AGENT_REF_PREFIX)) {
    const agentId = trimmed.slice(OWNED_AGENT_REF_PREFIX.length)
    if (!agentId || agentId.includes('/')) return null
    return { kind: 'owned', agentId }
  }

  if (trimmed.startsWith(SHARED_AGENT_REF_PREFIX)) {
    const remainder = trimmed.slice(SHARED_AGENT_REF_PREFIX.length)
    const parts = remainder.split('/')
    if (parts.length !== 2) return null
    const [ownerUsername, slug] = parts
    if (!ownerUsername || !slug) return null
    return { kind: 'shared', ownerUsername, slug }
  }

  return null
}

export function isCanonicalAgentRef(value: string): boolean {
  return parseAgentRef(value) !== null
}

// Minimal shape of an "accessible agent" needed to resolve a selector to a human name. The store's
// AccessibleAgentItem (src/store/agents.ts) is structurally assignable to this, so both the Advanced
// WorkflowEditor and the simple-mode Automations lens can share one resolver instead of duplicating it.
export interface ResolvableAgentItem {
  ref: string
  kind: 'owned' | 'shared'
  title: string
  agentId?: string
}

// Resolve any agent selector to its catalog item. A selector may be a canonical ref
// (`agent/<id>` or `shared/<owner>/<slug>`), a legacy owned-agent name, or — when `matchBareId` is
// set — a raw agent id (e.g. a workflow's `mainAgentId`). Returns undefined when nothing matches.
export function findAgentItemBySelector<T extends ResolvableAgentItem>(
  selector: string | null | undefined,
  items: T[],
  opts: { matchBareId?: boolean } = {}
): T | undefined {
  const trimmed = (selector ?? '').trim()
  if (!trimmed) return undefined
  if (isCanonicalAgentRef(trimmed)) {
    const byRef = items.find((item) => item.ref === trimmed)
    if (byRef) return byRef
  }
  if (opts.matchBareId) {
    const byId = items.find((item) => item.agentId === trimmed)
    if (byId) return byId
  }
  return items.find((item) => item.kind === 'owned' && item.title === trimmed)
}

// Display name for a selector, falling back to the raw selector when it can't be resolved (e.g. a
// deleted agent) so the UI degrades to the original string rather than rendering blank.
export function agentTitleForSelector(
  selector: string | null | undefined,
  items: ResolvableAgentItem[],
  opts: { matchBareId?: boolean } = {}
): string {
  return findAgentItemBySelector(selector, items, opts)?.title ?? (selector ?? '').trim()
}
